import React from 'react'
import { ControlledTextInput } from 'transfix-form/components'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { IMotorCarrier } from 'apps/fleet-planner/types'
import { useAddTrailers } from '../hooks/useAddTrailer'
import { useAddTrailerForm } from '../hooks/useAddTrailerForm'
import { ITrailerSchema } from '../schema/TrailerSchema'

interface IAddTrailerModalProps extends Omit<IModalBaseProps, 'modalType'> {
  carriers: IMotorCarrier[]
  onCancel: () => void
  onSubmit: (newTrailerId: string | undefined, error?: string) => void
}

const useStyles = makeStyles(() => ({
  container: {
    '& .MuiFormControl-fullWidth': {
      paddingBottom: 22,
    },
  },
}))

const AddTrailerModal = ({
  carriers,
  onCancel,
  onSubmit,
  open = true,
  ...props
}: IAddTrailerModalProps) => {
  const classes = useStyles()
  const methods = useAddTrailerForm()
  const { control, errors, handleSubmit, setError } = methods
  const { addTrailer, loading: loadingCreateTrailer } = useAddTrailers()
  const carrierOptions = carriers.map(item => ({
    value: item.id,
    label: item.name,
  }))

  const submitHandler = handleSubmit(async (formParams: ITrailerSchema) => {
    const response = await addTrailer(formParams)
    if (response.errors) {
      if (response.errors === 'already exists') {
        setError('trailerNumber', {
          type: 'manual',
          shouldFocus: true,
          message: 'This trailer number already exists. Please enter another one.',
        })
      } else {
        onSubmit(undefined, response.errors)
      }
    } else {
      onSubmit(response.id)
    }
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onCancel()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
      disabled: loadingCreateTrailer,
    },
  ]

  return (
    <ModalBase
      dataTest='AddTrailerModal'
      modalType={MODAL_TYPES.form}
      size={4}
      open={open}
      actions={actions}
      title='Add Trailer'
      {...props}>
      <Box mb={2} className={classes.container}>
        <ControlledSelect
          name='carrier'
          control={control}
          defaultValue={carrierOptions[0].value}
          SelectProps={{
            dataTest: 'carrier-select',
            label: 'Carrier',
            required: true,
            options: [...carrierOptions],
            validationMessage: errors?.carrier?.message,
            error: !!errors?.carrier?.message,
            large: true,
          }}
        />
        <ControlledTextInput
          control={control}
          name='trailerNumber'
          defaultValue=''
          TextInputProps={{
            label: 'Trailer Number',
            required: true,
            large: true,
            validationMessage: errors?.trailerNumber?.message,
            error: !!errors?.trailerNumber?.message,
          }}
        />
        <ControlledTextInput
          control={control}
          name='trailerType'
          defaultValue=''
          TextInputProps={{
            label: 'Equipment Type',
            large: true,
            validationMessage: errors?.trailerType?.message,
            error: !!errors?.trailerType?.message,
          }}
        />
        <ControlledTextInput
          control={control}
          name='trailerLength'
          defaultValue=''
          TextInputProps={{
            label: 'Size',
            large: true,
            validationMessage: errors?.trailerLength?.message,
            error: !!errors?.trailerLength?.message,
          }}
        />
      </Box>
    </ModalBase>
  )
}

export default AddTrailerModal
